/**
 * A birthday out of a Malaysian IC.
 *
 * The first six digits of an IC are the date of birth as YYMMDD — 930503 is the
 * 3rd of May 1993 — so the birthday never needs asking for; it is already in the
 * number the customer wrote on the agreement. (user 2026-10-21: "birthday help me
 * check from system ic number. usually like 930503 is 1993-05-03 YYYY-MM-DD")
 *
 * ICs reach us in every shape a person can type one: 931210-10-5624, 640905 07 5804,
 * 551030075339, and the occasional 930823- where somebody stopped halfway. So this
 * works off the DIGITS alone and never off the punctuation.
 *
 * Two digits cannot say which century, so the rule is: read it as 20YY, and if that
 * lands in the future read it as 19YY instead. Nobody in the system was born
 * tomorrow, and the oldest customers are comfortably inside 1925-2025.
 *
 * Anything that is not a real date is rejected rather than guessed — the live data
 * holds 2313213231 and 9333333333-33303, which would otherwise come back as a
 * 13th and a 33rd month. A rejected IC shows as a blank birthday, which is honest;
 * a wrong birthday on a greeting is worse than none.
 */

/** The six leading digits, or null when there are not six of them. */
function dobDigits(ic) {
    const digits = String(ic || '').replace(/\D/g, '');
    return digits.length >= 6 ? digits.slice(0, 6) : null;
}

/**
 * 'YYYY-MM-DD' for an IC, or null when the IC carries no readable date.
 * The format is the one the user asked for, and it sorts as a string.
 */
export function icBirthday(ic, today = new Date()) {
    const six = dobDigits(ic);
    if (!six) return null;

    const yy = Number(six.slice(0, 2));
    const mm = Number(six.slice(2, 4));
    const dd = Number(six.slice(4, 6));

    // 20YY unless that has not happened yet, in which case 19YY.
    const thisCentury = new Date(Date.UTC(2000 + yy, mm - 1, dd));
    const future = thisCentury.getTime() > Date.UTC(
        today.getFullYear(), today.getMonth(), today.getDate(),
    );
    const year = future ? 1900 + yy : 2000 + yy;

    // Round-trip the date to throw out a 13th month or a 31st of February: the Date
    // constructor rolls those forward silently, so only an unchanged date is real.
    const d = new Date(Date.UTC(year, mm - 1, dd));
    if (d.getUTCFullYear() !== year || d.getUTCMonth() !== mm - 1 || d.getUTCDate() !== dd) {
        return null;
    }

    const p = (n) => String(n).padStart(2, '0');
    return `${year}-${p(mm)}-${p(dd)}`;
}

/** Whole years old on `today`, or null when the IC carries no readable date. */
export function icAge(ic, today = new Date()) {
    const iso = icBirthday(ic, today);
    if (!iso) return null;
    const [y, m, d] = iso.split('-').map(Number);
    let age = today.getFullYear() - y;
    const beforeBirthday =
        today.getMonth() + 1 < m ||
        (today.getMonth() + 1 === m && today.getDate() < d);
    if (beforeBirthday) age -= 1;
    return age >= 0 ? age : null;
}

/**
 * Days until the next birthday — 0 on the day itself. Null when unreadable.
 * Here so a list can sort by "coming up" without every caller doing the leap-year
 * arithmetic again.
 */
export function daysToBirthday(ic, today = new Date()) {
    const iso = icBirthday(ic, today);
    if (!iso) return null;
    const [, m, d] = iso.split('-').map(Number);
    const startOfToday = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
    let next = Date.UTC(today.getFullYear(), m - 1, d);
    if (next < startOfToday) next = Date.UTC(today.getFullYear() + 1, m - 1, d);
    return Math.round((next - startOfToday) / 86400000);
}

export default icBirthday;
